import { useState } from 'react';

import { Modal } from 'cpool-components/general/Modal';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { getAssignedUserSlots } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { LuCircleX, LuSave } from 'react-icons/lu';

import type { AssignedUsersLead } from './AssignedUserSlots';
import { ChangeAssignedUserRow } from './ChangeAssignedUserRow';
import { getAdviserName } from './getAdviserName';
import { useChangeAssignedUserMutation } from './useChangeAssignedUserMutation';
import { useLeadAdvisers } from './useLeadAdvisers';

type Props = {
    lead: AssignedUsersLead;
    onClose: () => void;
    open: boolean;
};

export const ChangeAssignedUsersModal: React.FC<Props> = ({ lead, onClose, open }) => {
    const { t } = useTranslation();
    const { data: advisers = [], isLoading } = useLeadAdvisers(lead);
    const { isPending, mutateAsync } = useChangeAssignedUserMutation(lead.id);
    const [selection, setSelection] = useState<Record<string, string>>({});
    const allSlots = getAssignedUserSlots(lead);
    const slots = allSlots.flatMap((slot) => (slot.category ? [{ ...slot, category: slot.category }] : []));
    const withoutSlot = allSlots.filter(({ category }) => !category);
    const changes = slots.flatMap(({ category, userId }) => {
        const newUserId = selection[userId];
        return newUserId && newUserId !== userId ? [{ category, holderId: userId, newUserId }] : [];
    });

    const nameOf = (userId: string) => getAdviserName(advisers, userId, t('LEADS.ASSIGNED_USERS.UNKNOWN_USER'));

    const close = () => {
        setSelection({});
        onClose();
    };

    // one command per changed slot, in order; stops at the first failure (its toast explains it)
    const saveInOrder = async ([next, ...rest]: typeof changes): Promise<boolean> => {
        if (!next) return true;
        try {
            await mutateAsync({ category: next.category, name: nameOf(next.newUserId), userId: next.newUserId });
        } catch {
            return false;
        }
        // drop the saved change so a retry only resends the rest
        setSelection((current) => Object.fromEntries(Object.entries(current).filter(([id]) => id !== next.holderId)));
        return saveInOrder(rest);
    };

    const save = async () => {
        if (await saveInOrder(changes)) close();
    };

    return (
        <Modal onClose={close} open={open} panelClassName="max-w-lg" title={t('LEADS.ASSIGNED_USERS.CHANGE_TITLE')}>
            <div className="flex flex-col gap-6" data-testid="change-assigned-users-modal">
                {/* no unknown-user flash while the names load */}
                {!isLoading && (
                    <>
                        <div className="grid grid-cols-[max-content_1fr] items-center gap-x-6 gap-y-2">
                            {slots.map((slot) => (
                                <ChangeAssignedUserRow
                                    advisers={advisers}
                                    disabled={isPending}
                                    holderName={nameOf(slot.userId)}
                                    key={slot.userId}
                                    onSelect={(userId) =>
                                        setSelection((current) => ({ ...current, [slot.userId]: userId }))
                                    }
                                    selectedUserId={selection[slot.userId] ?? slot.userId}
                                    slot={slot}
                                />
                            ))}
                        </div>
                        {withoutSlot.length > 0 && (
                            <div className="flex flex-col gap-1" data-testid="change-assigned-users-without-slot">
                                <span className="text-xs text-grey">{t('LEADS.ASSIGNED_USERS.NOT_IN_TEAM_STEP')}</span>
                                {withoutSlot.map(({ userId }) => (
                                    <span
                                        className="text-sm text-midnight"
                                        data-testid={`change-assigned-user-without-slot-${userId}`}
                                        key={userId}
                                    >
                                        {nameOf(userId)}
                                    </span>
                                ))}
                            </div>
                        )}
                    </>
                )}
                <div className="flex justify-end gap-2">
                    <Button
                        disabled={isPending}
                        iconLeft={<LuCircleX />}
                        onClick={close}
                        variant={ButtonVariant.SECONDARY}
                    >
                        {t('BUTTONS.CANCEL')}
                    </Button>
                    <Button
                        data-testid="change-assigned-users-save"
                        disabled={!changes.length || isPending}
                        iconLeft={<LuSave />}
                        onClick={save}
                        variant={ButtonVariant.PRIMARY}
                    >
                        {t('BUTTONS.SAVE')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
